import { useCallback, useEffect, useState } from "react";

export function useAsync<T>(loader: () => Promise<T>, dependencies: readonly unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await loader());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, dependencies);
  useEffect(() => { void refresh(); }, [refresh]);
  return { data, loading, error, refresh, setData };
}

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

export function useCountdown(targetMs: number | null, vibrate = true) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!targetMs) { setRemaining(0); return; }
    let notified = false;
    const tick = () => {
      const value = Math.max(0, Math.ceil((targetMs - Date.now()) / 1000));
      setRemaining(value);
      if (value === 0 && !notified) {
        notified = true;
        if (vibrate) navigator.vibrate?.([250, 120, 250]);
      }
    };
    tick();
    const interval = window.setInterval(tick, 250);
    return () => window.clearInterval(interval);
  }, [targetMs, vibrate]);
  return remaining;
}

export function useElapsedSeconds(startedAt: string | null, completedAt: string | null) {
  const calculate = () => startedAt
    ? Math.max(0, Math.floor(((completedAt ? Date.parse(completedAt) : Date.now()) - Date.parse(startedAt)) / 1000))
    : 0;
  const [elapsed, setElapsed] = useState(calculate);
  useEffect(() => {
    setElapsed(calculate());
    if (!startedAt || completedAt) return;
    const interval = window.setInterval(() => setElapsed(calculate()), 1000);
    return () => window.clearInterval(interval);
  }, [startedAt, completedAt]);
  return elapsed;
}
